import type { FormSchema, FormFieldDef, AddressValue } from '@hcc/types';
import {
  STATIC_FIELD_TYPES,
  MASKABLE_FIELD_TYPES,
  isMaskComplete,
  maskPlaceholder,
  asAddress,
  ADDRESS_SUBFIELDS,
  conditionMet,
} from '@hcc/types';
import type { FieldInvalid } from './FieldView';
import type { FormValues, FieldValue } from './formValues';

/** True when the field's visible_if condition is met (or absent). */
export function isFieldVisible(field: FormFieldDef, values: FormValues): boolean {
  if (!field.visible_if) return true;
  const v = values[field.visible_if.field];
  const eq = field.visible_if.equals;
  if (Array.isArray(v)) return v.some((x) => eq.includes(String(x)));
  return eq.includes(String(v ?? ''));
}

function isEmpty(v: FieldValue | undefined): boolean {
  if (v === undefined || v === null || v === '') return true;
  if (Array.isArray(v)) return v.length === 0;
  return false;
}

/** True when the schema collects special-category data, so consent is needed. */
export function schemaNeedsConsent(schema: FormSchema): boolean {
  return schema.pages.some((p) => p.fields.some((f) => f.special_category));
}

export interface ValidateFormInput {
  schema: FormSchema;
  values: FormValues;
  /** Field key -> pending signature captures (base64, submitted with the form). */
  signatureFiles: Record<string, readonly unknown[]>;
  /** Field key -> files already persisted into the live draft. */
  savedFiles: Record<string, readonly unknown[]>;
  /** Field key -> files on file from the last submission. */
  priorFiles: Record<string, readonly unknown[]>;
  consent: boolean;
  /**
   * Filling under a mobilisation-day scope: fields marked
   * optional_on_event_day drop their required check.
   */
  relaxEventDay?: boolean;
}

export interface ValidateFormResult {
  messages: string[];
  invalid: Record<string, FieldInvalid>;
  consentInvalid: boolean;
}

/**
 * Full client-side validation pass over a form's visible pages: required
 * fields (including files / signatures satisfied by a prior submission),
 * address sub-fields, and mask completeness. Mirrors the server checks so
 * the inductee fixes everything in one round.
 */
export function validateFormValues(input: ValidateFormInput): ValidateFormResult {
  const { schema, values, signatureFiles, savedFiles, priorFiles, consent, relaxEventDay } =
    input;
  const messages: string[] = [];
  const invalid: Record<string, FieldInvalid> = {};
  for (const pg of schema.pages) {
    if (!conditionMet(pg.visible_if, values)) continue;
    for (const f of pg.fields) {
      if (STATIC_FIELD_TYPES.includes(f.type) || !isFieldVisible(f, values)) continue;
      if (f.required && !(relaxEventDay && f.optional_on_event_day)) {
        if (f.type === 'file_upload' || f.type === 'signature' || f.type === 'damage_markup') {
          // Signatures and damage markups live in `signatureFiles` (base64
          // until submit); uploads persist into `savedFiles`. Either, or a
          // prior submission's file, satisfies.
          const current =
            f.type === 'file_upload' ? savedFiles[f.key]?.length : signatureFiles[f.key]?.length;
          const provided = (current ?? 0) > 0 || (priorFiles[f.key]?.length ?? 0) > 0;
          if (!provided) {
            messages.push(`"${f.label}" is required`);
            invalid[f.key] = { whole: true };
          }
        } else if (f.type === 'address') {
          const a = asAddress(values[f.key]);
          const missing = ADDRESS_SUBFIELDS.filter(
            (s) => s.requiredPart && a[s.key as keyof AddressValue].trim() === '',
          ).map((s) => s.key);
          if (missing.length > 0) {
            messages.push(`"${f.label}" needs a 1st line, city and post code`);
            invalid[f.key] = { parts: missing };
          }
        } else if (isEmpty(values[f.key])) {
          messages.push(`"${f.label}" is required`);
          invalid[f.key] = { whole: true };
        }
      }
      // Format check applies whenever a masked field has been filled, even if
      // it's optional.
      const mask = f.mask && MASKABLE_FIELD_TYPES.includes(f.type) ? f.mask : undefined;
      const v = values[f.key];
      if (mask && typeof v === 'string' && v !== '' && !isMaskComplete(mask, v)) {
        messages.push(`"${f.label}" must match the format ${maskPlaceholder(mask)}`);
        invalid[f.key] = { ...invalid[f.key], whole: true };
      }
    }
  }
  const consentInvalid = schemaNeedsConsent(schema) && !consent;
  if (consentInvalid) messages.push('Please agree to the consent statement.');
  return { messages, invalid, consentInvalid };
}
